// SearchbleMultiSelect
// Rails fields_forのように複数のセレクトボックスをReactで表現するためのコンポネント
// 
//

import React from 'react'
import Select, { ActionMeta } from 'react-select'

// Railsのfields_forで設定されるname属性パターンの解析・生成用
import {
  RailsFormElementNamePath,
  RailsFormElementNamePathPrimitives,
} from '../utils/rails_form_element_name_path'

// Railsのfields_forで作成される要素の再現やデータ追加・削除操作サポート
import {
  useRailsDynamicFormListContext,
  RailsDynamicFormListContext,
  RailsDynamicFormListRecord,
  RailsDynamicFormListIdElement,
  RailsDynamicFormListDestroyElement,
  RailsDynamicFormListDisplaytOrderElement,
} from '../utils/rails_dynamic_form_list';

// ドラッグアンドロップで並び替え用
import {
  DndContext,
  closestCenter,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// 再帰的な読み取り専用型
import { DeepReadonly } from '../utils/core';

/** react-selectのvalueの型 */
type SelectOptionRowValueType = string | number;

/** react-selectの1個分構造 */
type SelectOptionRow = { value: SelectOptionRowValueType, label: string };

/** 1行分のセレクトボックス Props */
interface SearchableMultiSelectRowProps<T = unknown> {
  /** クラス属性 */
  className?: string | null;

  /** select要素のクラス属性 */
  selectClassName?: string | null;

  /** Rails fields_forのname属性パス構造 */
  name: RailsFormElementNamePath;

  /** セレクトボックスの値、レコードID、表示順(display_order)、削除フラグ(_destroy)などのデータ */
  record: DeepReadonly<RailsDynamicFormListRecord<T>>;

  /** セレクトボックスの順番 */
  index: number;

  /** 入力必須か */
  required: boolean;

  /** プルダウンで表示する一覧 */
  options: DeepReadonly<SelectOptionRow[]>;

  /** 未入力時に表示する文字列 */
  placeholder?: string | null;

  /** idのhiddenなInput要素を配置するか？ */
  useIdHiddenInput?: boolean | null;

  /** _destroyのhiddenなInput要素を配置するか？ */
  useDestroyHiddenInput?: boolean | null;

  /** display_orderのhiddenなInput要素を配置するか？ */
  useDisplayOrderHiddenInput?: boolean | null;

  /** 既に選択されている項目をプルダウンリストから取り除くか */
  hideSelectedOptions?: boolean | null;

  /** データの参照・追加・削除・操作インターフェース */
  context: RailsDynamicFormListContext<T>;
}

/** 複数行のセレクトボックス Props
 ** 主にRailsのViewから利用することを想定 => <%= react_component('SearchableMultiSelect', props) %> 
 */
export interface SearchableMultiSelectProps<T = unknown> {
  /** クラス属性 */
  className?: string | null;

  /** 1行分の要素に指定するクラス属性 */
  rowClassName?: string | null;

  /** select要素のクラス属性 */
  selectClassName?: string | null;

  /** fields_forのname属性パス構造 文字列または配列
   ** 文字列の例： 'school[nearby_school_relations_attributes][0][school_id]'
   ** 配列の例： ['school', 'nearby_school_relations_attributes', 0, 'school_id'] 
  */
  name: string | DeepReadonly<RailsFormElementNamePathPrimitives>;

  /** 入力必須か */
  required: boolean;

  /** プルダウンで表示する一覧*/
  options: DeepReadonly<SelectOptionRow[]>;

  /** セレクトボックス 一覧 
   ** 値(value)、レコードID(id)、表示順(display_order)、削除フラグ(_destroy) などのデータリスト
  */
  rows: DeepReadonly<RailsDynamicFormListRecord<T>[]>;

  /** 未入力時に表示する文字列 */
  placeholder?: string | null;

  /** idのhiddenなInput要素を配置するか？ */
  useIdHiddenInput?: boolean | null;

  /** _destroyのhiddenなInput要素を配置するか？ */
  useDestroyHiddenInput?: boolean | null;

  /** display_orderのhiddenなInput要素を配置するか？ */
  useDisplayOrderHiddenInput?: boolean | null;

  /** 二重登録できないようにするか（既に選択されている項目をプルダウンリストから取り除くか） */
  hideSelectedOptions?: boolean | null;

  /** 動的なフォーム部品の追加や削除などを管理する仕組み 親子コンポーネントで共有 */
  context?: RailsDynamicFormListContext<unknown> | null;
}

/** 二重登録できないようにする
 ** 既に選択されている項目はセレクトボックスのプルダウンリストに表示しないようにフィルター処理する  
 * @param currentRecord 現在選択中の行
 * @param records すべての行
 * @param options ドロップダウンリスト
 * @param toComparableValue optionsのvalueと比較対象データをrecordから取得(変換)するコールバック関数
*/
const removeAlreadySelectedOptions = <T,>(
  currentRecord: RailsDynamicFormListRecord<T>,
  records: DeepReadonly<RailsDynamicFormListRecord<T>[]>,
  options: DeepReadonly<SelectOptionRow[]>,
  toComparableValue: (record: DeepReadonly<RailsDynamicFormListRecord<T>>) => SelectOptionRowValueType,
) => {
  const alreadySelected = new Set(records.map(record => toComparableValue(record)));
  return options.filter(option => !alreadySelected.has(option.value) || option.value == currentRecord.value)
}

/** 1行分のセレクトボックス コンポーネント */
function SearchableMultiSelectRow({
  className: _className, selectClassName,
  name, record, index, required, options: _options, placeholder = null,
  useIdHiddenInput, useDisplayOrderHiddenInput, useDestroyHiddenInput,
  hideSelectedOptions, context
}: SearchableMultiSelectRowProps) {
  // Selectにはlabelも含めた指定が必要
  const _value = _options.find(o => o.value == String(record.value));

  // ドラッグによる並び替え用
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: context.getUniqueKey(record) });

  /** */
  const className: string[] = [];

  // カスタムクラス属性
  if (_className) {
    className.push(_className);
  }

  // 削除予約スタイル
  if (record._destroy) {
    className.push('remove-reserved');
  }

  /** 全体スタイル */
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
  };

  /** 移動ハンドルボタンスタイル */
  const moveButtonStyle = {
    cursor: isDragging ? "grabbing" : "grab",
  };

  /** セレクトボックス値変更時 */
  const handleChangeValue = (newValue: SelectOptionRow | null, actionMeta: ActionMeta<SelectOptionRow>) => {
    if (newValue) {
      context.setValue(index, newValue.value);
    }
  }

  /** 削除クリック時 */
  const handleClickRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (record._destroy) {
      context.clearRemoveFlag(index);
    }
    else {
      context.remove(index);
    }
  }

  /** ドロップダウンリストの内容 */
  const options = (() => {
    if (hideSelectedOptions) {
      // 既に選択されている項目を取り除く
      return removeAlreadySelectedOptions(record, context.records, _options, record => record.value as SelectOptionRowValueType)
    }
    else {
      // 全部
      return _options;
    }
  })();

  // レイアウト
  return <div className={className.join(' ')} style={style} ref={setNodeRef} {...attributes} >
    {/* 移動ハンドル */}
    <button type="button" style={moveButtonStyle} {...listeners}>⋮</button>

    {/* セレクトボックス */}
    <Select
      isDisabled={record._destroy}
      isSearchable={true}
      name={name.make({ index }).toString() /* ← Railsのname属性パターンの行数(index)に当たる部分を書き換えて設定する */}
      required={required}
      options={options}
      value={_value ?? null}
      placeholder={placeholder}
      className={selectClassName ?? ''}
      onChange={handleChangeValue}
    />

    {/* 削除ボタン */}
    <button type="button" onClick={handleClickRemove}>{record._destroy ? '❌' : '➖'}</button>

    {/* ↓ レコードのid、削除フラグ(_destroy)、表示順(display_order) のhidden要素を配置 */}
    {useIdHiddenInput && <RailsDynamicFormListIdElement index={index} record={record} name={name} />}
    {useDestroyHiddenInput && <RailsDynamicFormListDestroyElement index={index} record={record} name={name} />}
    {useDisplayOrderHiddenInput && <RailsDynamicFormListDisplaytOrderElement index={index} record={record} name={name} />}
  </div>
}

/** 複数行のセレクトボックス 
 ** 主にRailsのViewから利用することを想定 例： <%= react_component('SearchableMultiSelect', props) %>  
*/
function SearchableMultiSelect({
  className, rowClassName, selectClassName,
  name, required, options, rows, placeholder = null,
  useIdHiddenInput, useDisplayOrderHiddenInput, useDestroyHiddenInput,
  hideSelectedOptions,
  context: _context,
}: SearchableMultiSelectProps) {
  /** 動的なフォーム部品の追加や削除などを管理する仕組み 親子コンポーネントで共有 */
  const context = _context ?? useRailsDynamicFormListContext({ firstRows: rows });

  /** セレクトボックス追加ボタン（上部） */
  const handleClickAddBefore = (e: React.MouseEvent<HTMLButtonElement>) => {
    // 先頭に追加
    context.add({ index: 0 });
  }

  /** セレクトボックス追加ボタン（下部） */
  const handleClickAddAfter = (e: React.MouseEvent<HTMLButtonElement>) => {
    // 末尾に追加
    context.add();
  }

  /** ドラッグ時 */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      /** 元の位置 */
      const from = context.getRecordIndexByUniqueKey(active.id as string) ?? 0;
      /** ドラッグオーバーされた位置（移動先） */
      const to = context.getRecordIndexByUniqueKey(over.id as string) ?? 0;
      // 移動処理
      context.move({ from, to });
    }
  };

  /** SortableContextのitems用 */
  const sortableContextItemList = context.records.map(record => context.getUniqueKey(record));

  // レイアウト
  return <div className={className ?? undefined}>
    {/* 上部 追加ボタン */}
    <button onClick={handleClickAddBefore} type="button">➕</button>

    {/* ドラッグ処理用 */}
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sortableContextItemList} strategy={verticalListSortingStrategy}>
        {/* 各行を描画 */}
        {context.records.map((record, index) => {
          return <SearchableMultiSelectRow
            key={context.getUniqueKey(record)}
            className={rowClassName ?? null}
            selectClassName={selectClassName ?? null}
            name={RailsFormElementNamePath.make(name)}
            record={record}
            index={index}
            required={required}
            options={options}
            placeholder={placeholder ?? null}
            useIdHiddenInput={useIdHiddenInput ?? null}
            useDisplayOrderHiddenInput={useDisplayOrderHiddenInput ?? null}
            useDestroyHiddenInput={useDestroyHiddenInput ?? null}
            hideSelectedOptions={hideSelectedOptions ?? null}
            context={context}
          />
        })}
      </SortableContext>
    </DndContext>

    {/* 下部 追加ボタン（要素が一つもない場合は2つ追加ボタンが並んでいると見栄えが悪いので表示しない） */}
    {context.records.length != 0 && <button onClick={handleClickAddAfter} type="button">➕</button>}
  </div>
}

export default SearchableMultiSelect
